import { alpha, type SxProps, type Theme } from '@mui/material/styles';
import { format } from 'date-fns';
import { BalanceType } from 'constants/balance.constants';
import { customerAccountDetailsTypes, moneyActivityTypes } from 'constants/customer-account.constants';
import { generalTranslation } from 'constants/global.constants';
import { timing } from 'constants/ui.constants';
import { WebhookMonitoringWebhookStatus, WebhookMonitoringDetailType } from 'constants/webhook-monitoring.constants';

type WebhookMonitoringStatusChipColor = 'default' | 'success' | 'error' | 'info' | 'warning';

export type WebhookBusinessSummaryRow = {
	labelKey: string;
	value: string;
	translateValue?: boolean;
};

/** Datetime with seconds for webhook monitoring table + modal only. Caller must supply a defined value; invalid dates propagate from date-fns `format`. */
export const formatWebhookMonitoringDateTime = (date: string | Date): string => {
	const parsedDate = date instanceof Date ? date : new Date(date);
	return format(parsedDate, timing.FULLDATE_WITH_SECONDS);
};

const toText = (value: unknown): string | null => {
	if (value === null || value === undefined || value === '') {
		return null;
	}

	return String(value);
};

const toFormattedDate = (value: any): string | null => {
	if (!value) {
		return null;
	}

	return formatWebhookMonitoringDateTime(value as string | Date);
};

const addSummaryRow = (
	rows: WebhookBusinessSummaryRow[],
	labelKey: string,
	value: string | null | undefined,
	options?: { translateValue?: boolean }
) => {
	if (!value) {
		return;
	}

	rows.push({ labelKey, value, translateValue: options?.translateValue });
};

const getCurrencyDisplayValue = (currency: any): string | null => {
	if (currency === null || currency === undefined || currency === '') {
		return null;
	}

	if (typeof currency === 'object') {
		return toText(currency.symbol) || toText(currency.code) || toText(currency.name);
	}

	return toText(currency);
};

const formatAmountWithCurrency = (amount: any, currency: any): string | null => {
	const amountText = toText(amount);
	const currencyText = getCurrencyDisplayValue(currency);

	if (!amountText && !currencyText) {
		return null;
	}

	return [amountText, currencyText].filter(Boolean).join(' ');
};

const resolveTranslatedStatus = (hebrewStatus: any, status: any): string | null | undefined => {
	const translatedStatus = toText(hebrewStatus);

	if (translatedStatus) {
		return translatedStatus;
	}

	if (status === null || status === undefined || status === '') {
		return null;
	}

	return generalTranslation[status];
};

const getPaymentStatusLabel = (businessData: any) =>
	resolveTranslatedStatus(businessData?.statusHebrew, businessData?.status);

const getFeeCurrency = (businessData: any) => businessData?.fee?.currency ?? businessData?.currency;

const addFeeSummaryRows = (rows: WebhookBusinessSummaryRow[], businessData: any) => {
	const fee = businessData.fee;

	addSummaryRow(
		rows,
		'page.customerAccounts.forms.payment.details.feeType',
		toText(businessData?.swiftFeeTypeHebrew)
	);
	addSummaryRow(
		rows,
		'page.customerAccounts.forms.payment.details.fee',
		formatAmountWithCurrency(fee.amount, getFeeCurrency(businessData))
	);
	addSummaryRow(
		rows,
		'page.customerAccounts.forms.payment.status.fee_status',
		resolveTranslatedStatus(fee.statusHebrew, fee.status)
	);
	addSummaryRow(rows, 'page.customerAccounts.forms.payment.accountManagementDetails.ccId', toText(fee.ccId));

	const feeReference = toText(fee.ccShortReference) || toText(fee.reference);
	addSummaryRow(rows, 'label.referenceNumber', feeReference);
	addSummaryRow(rows, 'page.customerAccounts.forms.payment.details.updatedAt', toFormattedDate(fee.updatedAt));
};

const addBalanceRefundSummaryRows = (rows: WebhookBusinessSummaryRow[], businessData: any) => {
	addSummaryRow(rows, 'page.customerAccounts.forms.balance_refund.reference', toText(businessData?.reference));
	addSummaryRow(
		rows,
		'page.customerAccounts.forms.balance_refund.amount',
		formatAmountWithCurrency(businessData?.amount, businessData?.currency)
	);
	addSummaryRow(
		rows,
		'page.customerAccounts.forms.balance_refund.status',
		resolveTranslatedStatus(businessData?.statusHebrew, businessData?.status)
	);
	addSummaryRow(rows, 'page.customerAccounts.forms.balance_refund.cc_id', toText(businessData?.ccId));

	const refundDate = toFormattedDate(businessData?.settlementDate) || toFormattedDate(businessData?.createdAt);
	addSummaryRow(rows, 'page.customerAccounts.forms.balance_refund.settlement_date', refundDate);
};

const addPaymentSummaryRows = (rows: WebhookBusinessSummaryRow[], businessData: any) => {
	addSummaryRow(rows, 'page.customerAccounts.forms.payment.details.reference', toText(businessData?.reference));
	addSummaryRow(
		rows,
		'page.customerAccounts.forms.payment.details.amount',
		formatAmountWithCurrency(businessData?.amount, businessData?.currency)
	);
	addSummaryRow(rows, 'page.customerAccounts.forms.payment.status.status', getPaymentStatusLabel(businessData));
	addSummaryRow(
		rows,
		'page.customerAccounts.forms.payment.details.typeOfPayment',
		toText(businessData?.typeOfPaymentHebrew)
	);
	addSummaryRow(
		rows,
		'page.customerAccounts.forms.payment.category.title',
		toText(businessData?.paymentCategoryDisplay)
	);
	addSummaryRow(rows, 'page.customerAccounts.forms.payment.details.purposeCode', toText(businessData?.purposeCode));
	addSummaryRow(
		rows,
		'page.customerAccounts.forms.payment.details.invoiceNumber',
		toText(businessData?.invoiceNumber)
	);
	addSummaryRow(
		rows,
		'page.customerAccounts.forms.payment.details.swiftNumber',
		toText(businessData?.beneficiary?.bicSwift)
	);
	addSummaryRow(
		rows,
		'page.customerAccounts.forms.payment.details.feeType',
		toText(businessData?.swiftFeeTypeHebrew)
	);
	addSummaryRow(
		rows,
		'page.customerAccounts.forms.payment.details.fee',
		formatAmountWithCurrency(businessData?.fee?.amount, getFeeCurrency(businessData))
	);
	addSummaryRow(
		rows,
		'page.customerAccounts.forms.payment.status.fee_status',
		resolveTranslatedStatus(businessData?.fee?.statusHebrew, businessData?.fee?.status)
	);
	addSummaryRow(
		rows,
		'page.customerAccounts.forms.payment.details.signedAt',
		toFormattedDate(businessData?.signerApprovalDate)
	);
	addSummaryRow(
		rows,
		'page.customerAccounts.forms.payment.accountManagementDetails.ccId',
		toText(businessData?.ccId)
	);

	const paymentDate =
		toFormattedDate(businessData?.settlementDate) ||
		toFormattedDate(businessData?.boApprovalDate) ||
		toFormattedDate(businessData?.updatedAt);
	addSummaryRow(rows, 'page.customerAccounts.forms.payment.details.settlementDate', paymentDate);
};

const addConversionSummaryRows = (rows: WebhookBusinessSummaryRow[], businessData: any) => {
	addSummaryRow(rows, 'page.customerAccounts.forms.conversion.reference.reference', toText(businessData?.reference));
	addSummaryRow(rows, 'page.customerAccounts.forms.conversion.benefit.benefit', toText(businessData?.benefit?.name));
	addSummaryRow(
		rows,
		'page.customerAccounts.forms.conversion.details.buyAmount',
		formatAmountWithCurrency(
			businessData?.pendingQuote?.altshulerBuyAmount,
			businessData?.pendingQuote?.clientBuyCurrency
		)
	);
	addSummaryRow(
		rows,
		'page.customerAccounts.forms.conversion.details.sellAmount',
		formatAmountWithCurrency(
			businessData?.pendingQuote?.altshulerSellAmount,
			businessData?.pendingQuote?.clientSellCurrency
		)
	);
	addSummaryRow(
		rows,
		'page.customerAccounts.forms.conversion.details.finalRate',
		toText(businessData?.pendingQuote?.finalRate)
	);
	addSummaryRow(
		rows,
		'page.customerAccounts.forms.conversion.details.altshulerRate',
		toText(businessData?.pendingQuote?.altshulerRate)
	);
	addSummaryRow(
		rows,
		'page.customerAccounts.forms.conversion.accountManagementDetails.ccId',
		toText(businessData?.ccId)
	);
	addSummaryRow(rows, 'page.customerAccounts.forms.conversion.createdBy', toText(businessData?.createdBy));
	addSummaryRow(rows, 'page.customerAccounts.forms.conversion.approverName', toText(businessData?.approverName));
	addSummaryRow(
		rows,
		'page.customerAccounts.forms.payment.details.updatedAt',
		toFormattedDate(businessData?.updatedAt)
	);
};

const addBalanceSummaryRows = (rows: WebhookBusinessSummaryRow[], businessData: any, webhookOperationType?: string) => {
	addSummaryRow(rows, 'page.customerAccounts.forms.balance.details.reference', toText(businessData?.reference));
	addSummaryRow(
		rows,
		'page.customerAccounts.forms.balance.details.amount',
		formatAmountWithCurrency(businessData?.amount, businessData?.currency)
	);
	if (webhookOperationType === BalanceType.DigitalTopping) {
		addSummaryRow(
			rows,
			'page.customerAccounts.forms.balance.accountManagement.iconductFeezbackId',
			toText(businessData?.iconductFeezbackId)
		);
	}
	addSummaryRow(rows, 'page.customerAccounts.forms.balance.accountManagement.ccId', toText(businessData?.ccId));
	addSummaryRow(rows, 'page.customerAccounts.forms.balance.details.approvedBy', toText(businessData?.approverName));

	const balanceDate =
		toFormattedDate(businessData?.settlementDate) ||
		toFormattedDate(businessData?.completedAt) ||
		toFormattedDate(businessData?.updatedAt);
	addSummaryRow(rows, 'page.customerAccounts.forms.balance.details.endDate', balanceDate);
};

const addPaymentDetailSummaryRows = (
	rows: WebhookBusinessSummaryRow[],
	businessData: any,
	webhookOperationType?: string
) => {
	if (webhookOperationType === moneyActivityTypes.PAYMENT_FEE) {
		addFeeSummaryRows(rows, businessData);
		return;
	}

	addPaymentSummaryRows(rows, businessData);
};

const addConversionDetailSummaryRows = (
	rows: WebhookBusinessSummaryRow[],
	businessData: any,
	webhookOperationType?: string
) => {
	if (webhookOperationType === moneyActivityTypes.CONVERSION_FEE) {
		addFeeSummaryRows(rows, businessData);
		return;
	}

	addConversionSummaryRows(rows, businessData);
};

const addHyperCreateCompanySummaryRows = (rows: WebhookBusinessSummaryRow[], businessData: any) => {
	addSummaryRow(rows, 'label.companyName', toText(businessData?.companyName));
	addSummaryRow(rows, 'page.newAccount.tables.hyperAccountNumber', toText(businessData?.hyperAccountNumber));
	addSummaryRow(rows, 'page.newAccount.tables.identityNumber', toText(businessData?.identityNumber));
	addSummaryRow(rows, 'page.newAccount.tables.accountNameHebrew', toText(businessData?.accountNameHebrew));
	addSummaryRow(rows, 'page.newAccount.tables.accountNameEnglish', toText(businessData?.accountNameEnglish));
	addSummaryRow(rows, 'page.webhookMonitoring.field.businessStatus', toText(businessData?.newAccountStatus));
	addSummaryRow(rows, 'label.id', toText(businessData?.newAccountId));
};

const addHyperCreditCardSummaryRows = (rows: WebhookBusinessSummaryRow[], businessData: any) => {
	addSummaryRow(rows, 'label.companyName', toText(businessData?.companyName));
	addSummaryRow(rows, 'page.newAccount.tables.hyperAccountNumber', toText(businessData?.hyperAccountNumber));
	addSummaryRow(rows, 'page.webhookMonitoring.field.businessStatus', toText(businessData?.creditCardStatus));
};

const webhookBusinessSummaryRowsByDetailType: Record<
	string,
	(rows: WebhookBusinessSummaryRow[], businessData: any, webhookOperationType?: string) => void
> = {
	[customerAccountDetailsTypes.PAYMENT]: addPaymentDetailSummaryRows,
	[customerAccountDetailsTypes.CONVERSION]: addConversionDetailSummaryRows,
	[customerAccountDetailsTypes.BALANCE]: addBalanceSummaryRows,
	[customerAccountDetailsTypes.BALANCE_WITHDRAWAL]: addBalanceSummaryRows,
	[customerAccountDetailsTypes.BALANCE_REFUND]: addBalanceRefundSummaryRows,
	[WebhookMonitoringDetailType.HyperCreateCompany]: addHyperCreateCompanySummaryRows,
	[WebhookMonitoringDetailType.HyperCreditCard]: addHyperCreditCardSummaryRows,
};

export const getWebhookMonitoringBusinessSummary = (
	detailType: string,
	businessData: any,
	webhookOperationType?: string
): WebhookBusinessSummaryRow[] => {
	const rows: WebhookBusinessSummaryRow[] = [];
	webhookBusinessSummaryRowsByDetailType[detailType](rows, businessData, webhookOperationType);

	return rows;
};

export const getWebhookMonitoringStatusChipColor = (status: string): WebhookMonitoringStatusChipColor => {
	switch (status) {
		case WebhookMonitoringWebhookStatus.Completed:
			return 'success';
		case WebhookMonitoringWebhookStatus.Failed:
		case WebhookMonitoringWebhookStatus.DeadLetter:
			return 'error';
		case WebhookMonitoringWebhookStatus.Processing:
		case WebhookMonitoringWebhookStatus.Published:
			return 'info';
		default:
			return 'default';
	}
};

/**
 * Muted, pastel status pill for webhook modals: low-opacity fill + hairline border (not saturated Mui success/error fills).
 */
export const getWebhookMonitoringStatusChipSx = (status: string, theme: Theme): SxProps<Theme> => {
	const kind = getWebhookMonitoringStatusChipColor(status);
	const mild = (main: string, fg: string) => ({
		backgroundColor: alpha(main, 0.11),
		color: fg,
		border: '1px solid',
		borderColor: alpha(main, 0.22),
		boxShadow: 'none',
	});

	if (kind === 'success') {
		const p: any = theme.palette.success;
		return mild(p.main, p.darker);
	}
	if (kind === 'error') {
		const p: any = theme.palette.error;
		return mild(p.main, p.darker);
	}
	if (kind === 'info') {
		const p: any = theme.palette.info;
		return mild(p.main, p.darker);
	}
	if (kind === 'warning') {
		const p: any = theme.palette.warning;
		return mild(p.main, p.darker);
	}
	return {
		backgroundColor: alpha(theme.palette.text.primary, 0.05),
		color: theme.palette.text.primary,
		border: '1px solid',
		borderColor: alpha(theme.palette.text.primary, 0.1),
		boxShadow: 'none',
	};
};

export const resolveGeneralTranslationLabel = (key: string): string => generalTranslation[key];
